/**
 * ============================================================================
 * [P3-05-E] BackupHistory - 백업 히스토리 컴포넌트
 *
 * 기능:
 * - 마지막 백업 날짜 표시
 * - 백업 권장 안내
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useMemo } from 'react'
import { formatDate } from '@/utils'
import styles from './Backup.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface BackupHistoryProps {
  lastBackupDate: Date | null
}

// ============================================================================
// 3. BackupHistory Component
// ============================================================================
export function BackupHistory({ lastBackupDate }: BackupHistoryProps) {
  // ========================================================================
  // 3-1. 백업 경과 일수 계산 (useMemo로 순수성 유지)
  // ========================================================================
  const daysSinceLastBackup = useMemo(() => {
    if (!lastBackupDate) return null
    const now = new Date()
    return Math.floor((now.getTime() - lastBackupDate.getTime()) / (1000 * 60 * 60 * 24))
  }, [lastBackupDate])

  // ========================================================================
  // 3-2. 백업 권장 여부 (7일 이상 경과)
  // ========================================================================
  const needsBackup = daysSinceLastBackup === null || daysSinceLastBackup >= 7

  // ========================================================================
  // 3-3. Render
  // ========================================================================
  return (
    <div className={styles.backupHistory}>
      {/* ================================================================
          섹션 제목
          ================================================================ */}
      <h4>백업 정보</h4>

      {/* ================================================================
          마지막 백업 정보
          ================================================================ */}
      <div className={styles.historyItem}>
        <span className={styles.label}>마지막 백업:</span>
        <span className={styles.value}>
          {lastBackupDate ? formatDate(lastBackupDate) : '기록 없음'}
        </span>
      </div>

      {/* ================================================================
          경과 일수
          ================================================================ */}
      {daysSinceLastBackup !== null && (
        <div className={styles.historyItem}>
          <span className={styles.label}>경과:</span>
          <span className={styles.value}>
            {daysSinceLastBackup === 0 ? '오늘' : `${daysSinceLastBackup}일 전`}
          </span>
        </div>
      )}

      {/* ================================================================
          백업 권장 안내
          ================================================================ */}
      {needsBackup && (
        <div className={styles.backupReminder}>
          <span className={styles.reminderIcon}>💡</span>
          <span>정기적인 백업을 권장합니다. 데이터 손실을 방지하세요!</span>
        </div>
      )}

      {/* ================================================================
          안내 문구
          ================================================================ */}
      <div className={styles.infoBox}>
        <h5>백업 파일 안내</h5>
        <ul>
          <li>백업 파일은 JSON 형식으로 저장됩니다.</li>
          <li>저장된 글, 스크립트, URL 링크가 포함됩니다.</li>
          <li>안전한 위치에 백업 파일을 보관하세요.</li>
        </ul>
      </div>
    </div>
  )
}

export default BackupHistory
